import {
  Activity,
  BarChart3,
  BookOpen,
  Bug,
  ClipboardCheck,
  ClipboardList,
  Database,
  FileOutput,
  FileSignature,
  FileText,
  FormInput,
  Globe,
  GraduationCap,
  LayoutDashboard,
  type LucideIcon,
  MessageSquare,
  Shield,
  ShieldCheck,
  Target,
} from "lucide-react";
import { caleAdministrare, GRUPURI_ADMINISTRARE } from "@/config/administrare";
import type { SessionStatus } from "@/types/common";
import type { EntityData } from "@/types/entity";
import type { NavigationData, NavigationPage } from "@/types/navigation";
import { RESULT_ENUM } from "../utils/nis2-classifier-v2";

export const NAVIGATION_DATA: NavigationData = {
  subsections: [
    {
      id: "status-entitate",
      title: "Status Entitate NIS2",
      icon: LayoutDashboard,
      defaultExpanded: true,
      pages: [
        {
          id: "ghid-nis2",
          title: "Ghid General NIS2",
          shortTitle: "Ghid NIS2",
          icon: BookOpen,
          url: "/ghid-nis2",
          status: "available",
        },
        {
          id: "dashboard-nis2",
          title: "Dashboard NIS2",
          shortTitle: "Dashboard",
          icon: LayoutDashboard,
          url: "/dashboard",
          status: "available",
          requiresAuth: true,
        },
      ],
    },
    // {
    //   id: "onboarding",
    //   title: "Onboarding",
    //   icon: GraduationCap,
    //   defaultExpanded: true,
    //   pages: [
    //     {
    //       id: "formular-notificare",
    //       title: "Formular Notificare",
    //       shortTitle: "Notificare",
    //       icon: FormInput,
    //       url: "/onboarding/formular-notificare",
    //       status: "available",
    //       requiresAuth: true,
    //     },
    //     {
    //       id: "decizie-inregistrare",
    //       title: "Decizie Înregistrare",
    //       shortTitle: "Decizie Înreg.",
    //       icon: FileSignature,
    //       url: "/onboarding/decizie-inregistrare",
    //       status: "locked",
    //     },
    //     {
    //       id: "onb-evaluare-nivel-risc",
    //       title: "Evaluare Nivel Risc",
    //       shortTitle: "Eval. Risc",
    //       icon: Target,
    //       url: "/onboarding/evaluare-nivel-risc",
    //       status: "locked",
    //     },
    //     {
    //       id: "onb-decizie-analiza-risc",
    //       title: "Decizie Analiză Risc",
    //       shortTitle: "Decizie AR",
    //       icon: FileSignature,
    //       url: "/onboarding/decizie-analiza-risc",
    //       status: "locked",
    //     },
    //     {
    //       id: "onb-evaluare-maturitate",
    //       title: "Evaluare Maturitate",
    //       shortTitle: "Eval. Maturitate",
    //       icon: ClipboardCheck,
    //       url: "/onboarding/evaluare-maturitate",
    //       status: "locked",
    //     },
    //     {
    //       id: "onb-decizie-evaluare-maturitate",
    //       title: "Decizie Evaluare Maturitate",
    //       shortTitle: "Decizie EVM",
    //       icon: FileSignature,
    //       url: "/onboarding/decizie-evaluare-maturitate",
    //       status: "locked",
    //     },
    //   ],
    // },
    {
      id: "inregistrare-entitate",
      title: "Înregistrare Entitate",
      icon: ClipboardList,
      defaultExpanded: true,
      pages: [
        {
          id: "ghid-notificare",
          title: "Ghid Notificare",
          shortTitle: "Ghid",
          icon: BookOpen,
          url: "/ghid-notificare",
          status: "available",
        },
        {
          id: "notificare-entitate",
          title: "Notificare Entitate",
          shortTitle: "Notificare",
          icon: FileText,
          url: "/identificare-raportare",
          status: "in-progress",
          hasSteps: true,
        },
        {
          id: "analiza-efect-perturbator",
          title: "Analiza Efect Perturbator",
          shortTitle: "Art.9",
          icon: Activity,
          url: "/analiza-efect-perturbator",
          status: "pending",
          requiresAuth: true,
          conditionalDisplay: (entityData, sessionId) => {
            if (!entityData || !sessionId) return false;
            return (
              entityData.form_session_id === sessionId &&
              (entityData.nis_classification?.classificationV2 ===
                RESULT_ENUM.IMPACT ||
                entityData.impact_analysis !== null)
            );
          },
        },
      ],
    },
    {
      id: "obligatii-entitate",
      title: "Obligații Entitate",
      icon: ShieldCheck,
      pages: [
        {
          id: "ghid-analiza-risc",
          title: "Ghid Analiză Risc",
          shortTitle: "Ghid AR",
          icon: BookOpen,
          url: "/ghid-analiza-risc",
          status: "available",
        },
        {
          id: "analiza-risc",
          title: "Analiză risc",
          shortTitle: "Analiză risc",
          icon: Target,
          url: "/analiza-risc",
          dynamicUrl: (sessionId) =>
            sessionId ? `/analiza-risc/${sessionId}` : null,
          status: "in-progress",
          hasSteps: true,
        },
        {
          id: "ghid-maturitate",
          title: "Ghid Maturitate",
          shortTitle: "Ghid EVM",
          icon: BookOpen,
          url: "/ghid-maturitate",
          status: "available",
        },
        {
          id: "evaluare-maturitate",
          title: "Evaluare Maturitate",
          shortTitle: "Maturitate",
          icon: ClipboardCheck,
          url: "/evaluare-maturitate",
          status: "in-progress",
          hasSteps: true,
        },
        {
          id: "plan-remediere",
          title: "Plan Remediere",
          shortTitle: "Remediere",
          icon: FileOutput,
          url: "/plan-remediere",
          status: "locked",
        },
      ],
    },
    {
      id: "comunicare-dnsc",
      title: "Comunicare DNSC",
      icon: MessageSquare,
      pages: [
        {
          id: "access-nis2",
          title: "Platforma NIS2",
          shortTitle: "NIS2",
          icon: Globe,
          externalLink: "https://nis2.dnsc.ro",
        },
        {
          id: "access-pnrisc",
          title: "Platforma PNRISC",
          shortTitle: "PNRISC",
          icon: Shield,
          externalLink: "https://pnrisc.dnsc.ro",
        },
        {
          id: "access-cvd",
          title: "CVD",
          shortTitle: "CVD",
          icon: Bug,
          externalLink: "https://cvd.dnsc.ro",
        },
      ],
    },
    {
      id: "administrare",
      title: "Administrare",
      icon: Database,
      defaultExpanded: false,
      // Generat din src/config/administrare.ts: acolo sunt taburile fiecarui ecran de administrare
      // si segmentele rutelor din App.tsx; aici nu se mai tine o a doua lista (D14).
      pages: GRUPURI_ADMINISTRARE.map(
        (grup): NavigationPage => ({
          id: `administrare-${grup.id}`,
          title: grup.titlu,
          shortTitle: grup.scurt,
          icon: grup.icon,
          url: grup.baza,
          status: "available",
          requiresAuth: true,
          adminOnly: true,
          children:
            grup.pagini.length > 0
              ? grup.pagini.map(
                  (pagina): NavigationPage => ({
                    id: `${grup.id}-${pagina.id}`,
                    title: pagina.eticheta,
                    icon: pagina.icon,
                    url: caleAdministrare(grup, pagina),
                    status: "available",
                    requiresAuth: true,
                    adminOnly: true,
                  })
                )
              : undefined,
        })
      ),
    },
  ],
};

// Legacy types and export for backward compatibility during migration
export type NavigationMenuItem = {
  title: string;
  url: string;
  icon: LucideIcon;
  description: string;
  requiresAuth?: boolean;
  disabled?: boolean;
  isSubpage?: boolean;
  conditionalDisplay?: (
    entityData: EntityData | null | undefined,
    sessionId: string | null,
    sessionStatus: SessionStatus | null
  ) => boolean;
};

export const NAVIGATION_MENU_ITEMS: NavigationMenuItem[] = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
    description: "Toate rapoartele",
  },
  {
    title: "Identificare și Raportare",
    url: "/identificare-raportare",
    icon: FileText,
    description: "Evaluare conformitate NIS 2",
  },
  {
    title: "Analiza efect perturbator",
    url: "/analiza-efect-perturbator",
    icon: Activity,
    description: "Evaluare impact potențial perturbator",
    requiresAuth: true,
    isSubpage: true,
    conditionalDisplay: (entityData, sessionId) => {
      if (!entityData || !sessionId) return false;
      return (
        entityData.form_session_id === sessionId &&
        (entityData.nis_classification?.classificationV2 ===
          RESULT_ENUM.IMPACT ||
          entityData.impact_analysis !== null)
      );
    },
  },
  {
    title: "Analiză de Risc",
    url: "/analiza-risc",
    icon: Shield,
    description: "Evaluare riscuri cibernetice",
  },
  {
    title: "Evaluare de Maturitate",
    url: "/evaluare-maturitate",
    icon: BarChart3,
    description: "Nivelul maturității cibernetice",
    disabled: false,
  },
];
